import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'react-apollo';
import { change, reduxForm, SubmissionError } from 'redux-form';
import get from 'lodash/get';
import omit from 'lodash/omit';
import ResourceWrapper from '../ResourceWrapper';
import AssessmentWrapper from '../AssessmentWrapper';
import { ASSIGN_MANAGE_FORM_NAME } from '../../utils/constants';
import reduxConnect from '../utils/redux';
import initializeForm from '../utils/initForm';
import getInitialState from './utils/getInitialState';
import handleFormUpdate from './utils/handleFormUpdate';
import {
  calculateClassesStats,
  getStudentsBySections,
  getSectionStudents,
  updateSectionsWithFormStructure,
  checkUpdatedStudents,
  addIsHardTimerIfChanged,
} from '../../utils';
import { AllAssignments } from 'chameleon-src/utility/utils';
import { getAssigneesWithStudents } from '../ResourceWrapper/utils';

const getResourceAssignments = (items) => {
  const openAssignments = items.reduce((acc, item) => {
    const itemAssignments = item.assignments || [];

    acc.push(
      ...itemAssignments.map((a) => ({
        ...a,
        assessment: {
          ...omit(items[0], ['assignments']),
        },
        assignees: [
          {
            id: a.section_id,
            name: a.section_name,
          },
        ],
      })),
    );

    return acc;
  }, []);

  return openAssignments;
};

/**
 * HOC for common data processing for assign/manage forms.
 * In https://academicmerit.atlassian.net/browse/FY-19867 we're dealing with multiple resources
 * that can be assigned using the assign/modal form.
 * */
const DataWrapper = (WrappedComponent) => {
  const AssessmentWrappedComponent = AssessmentWrapper(WrappedComponent);
  const ResourceWrappedComponent = ResourceWrapper(WrappedComponent);

  const InnerComponent = (props) => {
    const {
      isAssign,
      isAssessmentForm,
      isResourceForm,
      assessmentDataCache,
      selectedGroups: propSelectedGroups,
      dispatch,
      formData,
      subject: { classes: subjectClasses },
      individualStudentAssignmentFlag,
      assignment,
      isFinalExam,
      isPreApSubject,
      handleShowMustAssignInfoModal,
      item: resourceItem,
      items: resourceItems,
      assessments,
      assessmentWithQuestionsData,
      handleSubmit,
      onSubmit,
      userData: { isAdmin, isCoordinator },
      allowHardTimer,
    } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [state, setState] = useState(getInitialState(props));
    const [canSubmit, setCanSubmit] = useState(false);
    const [classesStats, dispatchClassesStats] = useState({});
    const [disabledStudents, setDisabledStudents] = useState([]);
    const [
      studentsFormBySectionDirtyStatus,
      setStudentsFormBySectionDirtyStatus,
    ] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const initialFormValues = useRef({});
    const initialFormStudents = useRef([]);
    const storedFormValues = useRef({});
    const autoSelectedGroups = useRef([]);
    const selectedGroups = useRef(
      propSelectedGroups || {
        assign: [],
        reAssign: [],
      },
    );
    const formComponentRef = useRef(null);
    const {
      regularSections,
      updateSections,
      progressSections,
      sectionsMinTimezone,
      submitBtnText,
      defaultTimezone,
      hasPreassignedBehavior,
      isPPC,
    } = state;
    const {
      students: formStudents,
      allAssignees,
      printToggle,
      scoreDisplay,
      startsAt,
      startsAtPart2,
      dueAt,
      timer,
      title,
      isSecure,
      hasScrambledOrder,
      studentScoring,
      testTakingLocation,
      duration,
      allowLateSubmission,
      isSoftTimer,
    } = formData;
    const isAdminOrCoordinator = isAdmin || isCoordinator;
    const assignmentId = assignment ? Number.parseInt(assignment.id, 10) : null;
    const sectionId = assignment
      ? Number.parseInt(assignment.assignees[0].id, 10)
      : null;

    let selectedSectionInitialStudents = [];
    let selectedSectionCurrentStudents = [];
    if (initialFormStudents.current && !!assignment) {
      selectedSectionInitialStudents = initialFormStudents.current.filter(
        (student) =>
          Number.parseInt(student.sectionId, 10) === sectionId &&
          Number.parseInt(student.assignmentId, 10) === assignmentId &&
          !student.fromOtherISA,
      );
    }
    const openAssignments = isAssessmentForm
      ? new AllAssignments(
          assessments,
          assessmentWithQuestionsData.id,
        ).getOpenAssignments()
      : getResourceAssignments(
          (resourceItems || []).length > 0 ? resourceItems : [resourceItem],
        );

    if (formStudents && !!assignment) {
      selectedSectionCurrentStudents = formStudents.filter(
        (student) =>
          Number.parseInt(student.sectionId, 10) === sectionId &&
          Number.parseInt(student.assignmentId, 10) === assignmentId &&
          !student.fromOtherISA,
      );
    }

    const selectedSectionCurrentStudentsLength =
      selectedSectionCurrentStudents.length;
    const isSelectedSectionStudentsChanged =
      selectedSectionInitialStudents.length !==
      selectedSectionCurrentStudentsLength;

    useEffect(() => {
      setIsLoading(false);
    }, [assessmentDataCache]);

    useEffect(() => {
      if (
        isAssign ||
        (isAssessmentForm && (isFinalExam || !assignment)) ||
        selectedSectionInitialStudents.length === 0
      ) {
        return;
      }

      const currentAssignmentHasNoSubStudents =
        selectedSectionCurrentStudents.length === 0;

      if (currentAssignmentHasNoSubStudents) {
        // Reset the students form data to initial state if currentAssignmentHasNoSubStudents is true
        const currentSection = assignment.assignees[0];
        const sectionAllStudents = getSectionStudents(
          subjectClasses,
          currentSection.id,
        );

        if (
          sectionAllStudents.length === selectedSectionInitialStudents.length
        ) {
          const newAssignees = [
            ...allAssignees,
            ...updateSectionsWithFormStructure([
              {
                ...currentSection,
                assignmentId: assignment.id,
              },
            ]),
          ];

          dispatch(change(ASSIGN_MANAGE_FORM_NAME, 'assignees', newAssignees));
          dispatch(
            change(ASSIGN_MANAGE_FORM_NAME, 'allAssignees', newAssignees),
          );
        }

        const students = [...formStudents, ...selectedSectionInitialStudents];
        dispatch(change(ASSIGN_MANAGE_FORM_NAME, 'students', students));
        calculateClassesStats({
          dispatch: dispatchClassesStats,
          stats: classesStats,
          students,
        });

        // Mark students form as untouched since
        studentsFormBySectionDirtyStatus[sectionId] = false;

        handleShowMustAssignInfoModal(true);
      }
    }, [formStudents]);

    const markStudentsFormAsDirty = () => {
      if (!studentsFormBySectionDirtyStatus[sectionId]) {
        setStudentsFormBySectionDirtyStatus({
          ...studentsFormBySectionDirtyStatus,
          [sectionId]: true,
        });
      }
    };

    const clearAssignees = () => {
      dispatch(change(ASSIGN_MANAGE_FORM_NAME, 'assignees', []));
      dispatch(change(ASSIGN_MANAGE_FORM_NAME, 'updateAssignees', []));
      dispatch(change(ASSIGN_MANAGE_FORM_NAME, 'allAssignees', []));
      dispatch(change(ASSIGN_MANAGE_FORM_NAME, 'students', []));

      calculateClassesStats({
        step: 'remove',
        dispatch: dispatchClassesStats,
        stats: classesStats,
        students: formStudents,
      });
    };

    const selectAllAssignees = () => {
      const allSections = regularSections.concat(updateSections);
      let availableStudents = getStudentsBySections(
        subjectClasses,
        allSections,
      );
      if (isAssign) {
        const disabledStudentIds = disabledStudents.map(
          (student) => student.id,
        );
        availableStudents = availableStudents.filter(
          (student) => !disabledStudentIds.includes(student.id),
        );
      }

      dispatch(change(ASSIGN_MANAGE_FORM_NAME, 'assignees', regularSections));
      dispatch(
        change(ASSIGN_MANAGE_FORM_NAME, 'updateAssignees', updateSections),
      );
      dispatch(change(ASSIGN_MANAGE_FORM_NAME, 'allAssignees', allSections));
      dispatch(change(ASSIGN_MANAGE_FORM_NAME, 'students', availableStudents));
      calculateClassesStats({
        dispatch: dispatchClassesStats,
        stats: classesStats,
        students: availableStudents,
      });
    };

    /**
     * Handler for checking if at least one options was updated.
     *
     * TODO use form prestine prop to verify changes fields
     * */
    const hasUpdatedOptions = () => {
      if (!assignment) {
        return (
          scoreDisplay ||
          startsAt ||
          startsAtPart2 ||
          timer ||
          printToggle ||
          hasScrambledOrder ||
          studentScoring ||
          title.trim() !== '' ||
          isSecure ||
          duration
        );
      }
      const { print_toggle: assignmentPrintToggle } = assignment;
      const accountStartsAtChange =
        (isResourceForm || !printToggle) &&
        initialFormValues.current.startsAt !== startsAt;
      const accountDueAtChange =
        (isResourceForm || !printToggle) &&
        ((startsAt && initialFormValues.current.dueAt !== dueAt) ||
          (initialFormValues.current.dueAt && !dueAt));
      const accountAllAssignees =
        !isFinalExam &&
        allAssignees.length !==
          get(initialFormValues.current, 'allAssignees', []).length;
      const accountPrintToggle =
        isAssessmentForm && printToggle !== assignmentPrintToggle;
      const hasUpdatedStudents =
        !isFinalExam &&
        individualStudentAssignmentFlag &&
        checkUpdatedStudents(
          formStudents.filter((student) => !student.fromOtherISA),
          selectedSectionInitialStudents,
          sectionId,
          assignmentId,
        );
      const accountLateSubmissionChange =
        allowLateSubmission !== initialFormValues.current.allowLateSubmission;
      const accountSoftTimerChange =
        isSoftTimer !== initialFormValues.current.isSoftTimer;

      return (
        initialFormValues.current &&
        (scoreDisplay !== initialFormValues.current.scoreDisplay ||
          accountStartsAtChange ||
          (initialFormValues.current.startsAtPart2 !== startsAtPart2 &&
            isPreApSubject) ||
          accountDueAtChange ||
          initialFormValues.current.timer !== timer ||
          accountPrintToggle ||
          isSecure !== initialFormValues.current.isSecure ||
          duration !== initialFormValues.current.duration ||
          studentScoring !== initialFormValues.current.studentScoring ||
          hasUpdatedStudents ||
          accountAllAssignees ||
          testTakingLocation !== initialFormValues.current.testTakingLocation ||
          accountLateSubmissionChange ||
          accountSoftTimerChange)
      );
    };

    const handleAssign = handleSubmit(async (...args) => {
      try {
        setIsSubmitting(true);

        if (isResourceForm) {
          const allSections = regularSections.concat(updateSections);
          const assignees = getAssigneesWithStudents(formData, allSections);
          await onSubmit(assignees, startsAt, dueAt, title);
        } else {
          await onSubmit(...args);
        }
      } catch (error) {
        throw new SubmissionError(error.errors);
      } finally {
        setIsSubmitting(false);
      }
    });

    const handleUpdateSingle = handleSubmit(async (args) => {
      let submitProps = {
        ...args,
        updateAll: false,
      };

      submitProps = addIsHardTimerIfChanged(
        args,
        initialFormValues.current,
        submitProps,
        allowHardTimer,
      );

      if (isAssessmentForm) {
        submitProps.matchingAssignments = openAssignments;
      }

      try {
        setIsSubmitting(true);
        await onSubmit(submitProps);
      } catch (error) {
        throw new SubmissionError(error.errors);
      } finally {
        setIsSubmitting(false);
      }
    });

    const handleUpdateAll = handleSubmit(async (values) => {
      let submitProps = {
        ...values,
        updateAll: true,
      };

      submitProps = addIsHardTimerIfChanged(
        values,
        initialFormValues.current,
        submitProps,
        allowHardTimer,
      );

      if (isAssessmentForm) {
        submitProps.matchingAssignments = openAssignments;
      }

      try {
        setIsSubmitting(true);
        await onSubmit(submitProps);
      } catch (error) {
        throw new SubmissionError(error.errors);
      } finally {
        setIsSubmitting(false);
      }
    });

    const submit = async () => {
      await formComponentRef.current.props.onSubmit();
    };

    const childProps = {
      globals: {
        submit,
        isLoading,
        state,
        setState,
        setCanSubmit,
        classesStats,
        dispatchClassesStats,
        disabledStudents,
        setDisabledStudents,
        autoSelectedGroups,
        selectedGroups,
        storedFormValues,
        defaultTimezone,
        initialFormValues,
        selectedSectionInitialStudents,
        hasUpdatedOptions,
        setIsSubmitting,
        updateForm: (childParams) => {
          handleFormUpdate({
            ...childParams,
            props,
            storedFormValues: storedFormValues.current,
          });
        },
        initForm: (childParams) =>
          initializeForm({
            isAssessmentForm,
            isResourceForm,
            data: {
              ...childParams,
              ...state,
              ...props,
              initialFormValues,
              initialFormStudents,
            },
          }),
      },
      ...props,
      regularSections,
      updateSections,
      progressSections,
      allSections: regularSections.concat(updateSections),
      clearAssignees,
      selectAllAssignees,
      canSubmit,
      submitBtnText,
      sectionsMinTimezone,
      isStudentsFormChanged:
        individualStudentAssignmentFlag && isSelectedSectionStudentsChanged,
      selectedSectionCurrentStudentsLength,
      sectionId,
      assignmentId,
      markStudentsFormAsDirty,
      isStudentsFormDirty: studentsFormBySectionDirtyStatus[sectionId],
      formComponentRef,
      disabledStudents,
      classesStats,
      openAssignments,
      handleAssign,
      handleUpdateSingle,
      handleUpdateAll,
      isSubmitting,
      isAdminOrCoordinator,
    };

    if (isAssessmentForm) {
      return (
        <AssessmentWrappedComponent
          {...childProps}
          hasPreassignedBehavior={hasPreassignedBehavior}
          isPPC={isPPC}
        />
      );
    } else if (isResourceForm) {
      return <ResourceWrappedComponent {...childProps} />;
    }

    return null;
  };

  InnerComponent.defaultProps = {
    isAssign: false,
  };

  // order matters. In order for graphql methods to access redux state, connect needs to be first
  return compose(
    reduxConnect,
    reduxForm({
      form: ASSIGN_MANAGE_FORM_NAME,
    }),
  )(InnerComponent);
};

DataWrapper.displayName = 'DataWrapper';

DataWrapper.propTypes = {
  dataType: PropTypes.oneOf(['assessment', 'ap_daily_video']).isRequired,
};

export default DataWrapper;
